import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Pencil, X } from 'lucide-react';
import { fieldClass, ghostBtn, labelClass, primaryBtn, selectField } from '../components/GatewayUi';
import { logGovernedMcp } from '../governedMcpLogger';
import { useGovernedMcp } from '../governedMcpStore';
import type { QuotaAssignment, QuotaAssignmentStatus } from '../governedMcpTypes';
import { formatQuotaMonth } from '../quotaUsage';

function parseLimit(value: string): number {
  return Number(value.replace(/[$,]/g, '').trim());
}

function kindLabel(resourceType: string | undefined): string {
  if (resourceType === 'mcp_tool') return 'Tools';
  if (resourceType === 'llm_gateway') return 'LLM Gateway';
  return 'Client';
}

export default function QuotaEditDialog({
  assignment,
  assignedTo,
  clientName,
  isLight,
  onClose,
  onToast,
}: {
  assignment: QuotaAssignment | null;
  assignedTo: string;
  clientName: string;
  isLight: boolean;
  onClose: () => void;
  onToast: (type: 'success' | 'error' | 'info', message: string) => void;
}) {
  const { setQuotaAssignment } = useGovernedMcp();
  const [limit, setLimit] = useState('');
  const [status, setStatus] = useState<QuotaAssignmentStatus>('active');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!assignment) return;
    setLimit(String(assignment.tokenLimit));
    setStatus(assignment.status === 'inactive' ? 'inactive' : 'active');
    setError(null);
  }, [assignment]);

  if (!assignment) return null;

  const resourceType = assignment.resourceType || 'client';
  const isTool = resourceType === 'mcp_tool';
  const limitLabel = isTool ? 'Max tool calls' : 'Monthly token limit';
  const titleName = assignment.name || assignedTo || 'this assignment';

  const fail = (reason: string) => {
    setError(reason);
    logGovernedMcp('warning', 'quota_listing_edit_blocked', {
      id: assignment.id,
      resourceType,
      reason,
    });
    onToast('error', reason);
  };

  const save = () => {
    if (!limit.trim()) {
      fail(`${limitLabel} is required.`);
      return;
    }
    const parsed = parseLimit(limit);
    if (!Number.isFinite(parsed) || parsed < 0) {
      fail(`${limitLabel} must be zero or a positive number.`);
      return;
    }
    const tokenLimit = Math.round(parsed);
    const result = setQuotaAssignment(
      assignment.scope,
      assignment.scopeId,
      assignment.clientId,
      tokenLimit,
      assignment.name,
      assignment.month,
      status,
      assignment.renewsMonthly,
      resourceType,
      assignment.resourceId || assignment.clientId
    );
    if (!result.ok) {
      fail(result.reason);
      return;
    }
    logGovernedMcp('app', 'quota_listing_edit_saved', {
      id: assignment.id,
      scope: assignment.scope,
      scopeId: assignment.scopeId,
      clientId: assignment.clientId,
      resourceId: assignment.resourceId,
      month: assignment.month,
      tokenLimit,
      status,
      resourceType,
      renewsMonthly: assignment.renewsMonthly,
    });
    onToast('success', `Quota updated for ${titleName} · ${formatQuotaMonth(assignment.month)}.`);
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-[220] flex items-center justify-center p-4">
      <button type="button" className="absolute inset-0 bg-neutral-950/60 backdrop-blur-[3px]" aria-label="Close" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="quota-edit-title"
        className={`relative w-full max-w-md rounded-2xl border shadow-2xl overflow-hidden ${
          isLight ? 'border-neutral-200 bg-white' : 'border-neutral-800 bg-neutral-950'
        }`}
      >
        <div className="px-5 pt-5 pb-4 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div
              className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
                isLight ? 'bg-indigo-50 text-indigo-600' : 'bg-indigo-500/15 text-indigo-300'
              }`}
            >
              <Pencil size={18} />
            </div>
            <div className="min-w-0">
              <h3 id="quota-edit-title" className={`text-base font-semibold tracking-tight ${isLight ? 'text-neutral-900' : 'text-white'}`}>
                Edit quota
              </h3>
              <p className={`text-sm mt-1.5 leading-relaxed ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                Update the {isTool ? 'tool-call limit' : 'token limit'} or status. Assignment identity stays the same.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`p-1.5 rounded-lg shrink-0 ${isLight ? 'hover:bg-neutral-100 text-neutral-400' : 'hover:bg-neutral-800 text-neutral-500'}`}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-5 pb-4 space-y-4">
          {error ? <p className="text-sm text-red-500">{error}</p> : null}
          <div className={`rounded-xl border px-3.5 py-3 text-sm ${isLight ? 'border-neutral-200 bg-neutral-50' : 'border-neutral-800 bg-neutral-900/70'}`}>
            <p className={`font-medium truncate ${isLight ? 'text-neutral-900' : 'text-white'}`}>{titleName}</p>
            <p className={`mt-1 text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
              {kindLabel(resourceType)} · {assignedTo} · {clientName} · {formatQuotaMonth(assignment.month)}
            </p>
          </div>
          <div>
            <label className={labelClass(isLight)} htmlFor="quota-edit-limit">
              {limitLabel}
            </label>
            <input
              id="quota-edit-limit"
              className={`${fieldClass(isLight)} tabular-nums`}
              inputMode="numeric"
              value={limit}
              onChange={(event) => setLimit(event.target.value)}
            />
          </div>
          <div>
            <label className={labelClass(isLight)} htmlFor="quota-edit-status">
              Status
            </label>
            <select
              id="quota-edit-status"
              className={`${selectField(isLight)} w-full`}
              value={status}
              onChange={(event) => setStatus(event.target.value as QuotaAssignmentStatus)}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        <div
          className={`px-5 py-3.5 flex justify-end gap-2 border-t ${
            isLight ? 'border-neutral-100 bg-neutral-50/80' : 'border-neutral-800 bg-neutral-900/80'
          }`}
        >
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={primaryBtn()} onClick={save}>
            Save changes
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
